import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";

const LOAD_STEPS = [
  { label: "Initialising system", key: "init" },
  { label: "Loading catalogue", key: "products" },
  { label: "Loading community", key: "posts" },
  { label: "Ready", key: "done" },
];

// Scanline CRT effect
function Scanlines() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={{
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.15) 3px, rgba(0,0,0,0.15) 4px)",
      }}
    />
  );
}

// Floating pixel particle
function Pixel({ x, y, delay, size, color }) {
  return (
    <motion.div
      className="absolute rounded-sm"
      style={{ left: `${x}%`, top: `${y}%`, width: size, height: size, background: color }}
      animate={{ y: [0, -18, 0], opacity: [0, 0.7, 0] }}
      transition={{ duration: 2.4 + delay, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}

const PIXELS = Array.from({ length: 22 }, (_, i) => ({
  x: (i * 37 + 7) % 97,
  y: (i * 53 + 11) % 90,
  delay: (i * 0.31) % 2.2,
  size: i % 3 === 0 ? 6 : i % 3 === 1 ? 4 : 3,
  color: i % 4 === 0 ? "#f5c518" : i % 4 === 1 ? "#a78bfa" : i % 4 === 2 ? "#38bdf8" : "#fb7185",
}));

export default function BootLoader({ onReady, preloadData }) {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [blinkOn, setBlinkOn] = useState(true);

  // Cursor blink
  useEffect(() => {
    const id = setInterval(() => setBlinkOn((b) => !b), 530);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Step 0 — init tick
      await tick(150);
      if (cancelled) return;
      setStep(1); setProgress(10);

      // Step 1 — fetch products & posts in parallel
      const [featured, used, posts] = await Promise.allSettled([
        api.get("/products", { params: { featured: true, limit: 5 } }),
        api.get("/products", { params: { condition: "used", limit: 4 } }),
        api.get("/posts/home"),
      ]);
      if (cancelled) return;
      setStep(2); setProgress(55);
      await tick(80);
      if (cancelled) return;
      setStep(3); setProgress(90);
      await tick(120);
      if (cancelled) return;
      setProgress(100);
      await tick(150);
      if (cancelled) return;

      // Pass data up so HomePage doesn't need to re-fetch
      preloadData({
        featured: featured.value?.data ?? [],
        used: used.value?.data ?? [],
        posts: posts.value?.data ?? [],
      });

      setExiting(true);
      await tick(400);
      if (!cancelled) onReady();
    }

    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentLabel = LOAD_STEPS[step]?.label ?? "Ready";

  return (
    <AnimatePresence>
      {!exiting ? (
        <motion.div
          key="bootloader"
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: "radial-gradient(ellipse at 50% 60%, #0f1535 0%, #05070f 100%)" }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.6, ease: "easeIn" }}
        >
          <Scanlines />

          {/* Ambient glow */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(245,197,24,0.06) 0%, transparent 70%)" }} />
          </div>

          {/* Floating pixels */}
          {PIXELS.map((p, i) => <Pixel key={i} {...p} />)}

          {/* Content card */}
          <motion.div
            className="relative flex flex-col items-center gap-8 px-10 py-12 rounded-3xl"
            style={{ background: "rgba(10,13,31,0.85)", border: "1px solid rgba(245,197,24,0.18)", backdropFilter: "blur(12px)", boxShadow: "0 0 60px rgba(245,197,24,0.08), inset 0 0 40px rgba(245,197,24,0.03)" }}
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {/* Logo / icon */}
            <div className="flex flex-col items-center gap-3">
              <motion.div
                animate={{ filter: ["drop-shadow(0 0 8px #f5c518)", "drop-shadow(0 0 24px #f5c518)", "drop-shadow(0 0 8px #f5c518)"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <img src="/logo.png" alt="GamesTrade" className="h-20 w-auto object-contain" />
              </motion.div>
              <span className="text-[9px] uppercase tracking-[0.35em] text-slate-500">System Boot v2.5</span>
            </div>

            {/* Progress bar */}
            <div className="w-72 flex flex-col gap-2">
              <div className="relative w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(245,197,24,0.15)" }}>
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ background: "linear-gradient(90deg, #b8960c, #f5c518, #ffe066)" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
                {/* Shimmer */}
                <motion.div
                  className="absolute inset-y-0 w-12 rounded-full"
                  style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)" }}
                  animate={{ left: ["-10%", "110%"] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.3 }}
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "#f5c518", fontFamily: "monospace" }}>
                  {currentLabel}
                  <span style={{ opacity: blinkOn ? 1 : 0 }}>_</span>
                </span>
                <span className="text-[10px] font-bold" style={{ color: "#f5c518", fontFamily: "monospace" }}>{progress}%</span>
              </div>
            </div>

            {/* Step list */}
            <div className="flex flex-col gap-1.5 w-72">
              {LOAD_STEPS.slice(0, -1).map((s, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <motion.div
                    key={s.key}
                    className="flex items-center gap-2.5"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: done || active ? 1 : 0.25, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <span className="w-3 h-3 rounded-sm flex items-center justify-center shrink-0" style={{ border: "1px solid rgba(245,197,24,0.3)", background: done ? "#f5c518" : "transparent" }}>
                      {done && (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path d="M1.5 4L3.5 6L6.5 2" stroke="#0a0d1f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      {active && (
                        <motion.div className="w-1.5 h-1.5 rounded-sm" style={{ background: "#f5c518" }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 0.8, repeat: Infinity }} />
                      )}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono" style={{ color: done ? "#f5c518" : active ? "#ffe066" : "#4a5568" }}>
                      {s.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            {/* Corner decorations */}
            <div className="absolute top-3 left-3 w-4 h-4 border-t border-l" style={{ borderColor: "rgba(245,197,24,0.4)" }} />
            <div className="absolute top-3 right-3 w-4 h-4 border-t border-r" style={{ borderColor: "rgba(245,197,24,0.4)" }} />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l" style={{ borderColor: "rgba(245,197,24,0.4)" }} />
            <div className="absolute bottom-3 right-3 w-4 h-4 border-b border-r" style={{ borderColor: "rgba(245,197,24,0.4)" }} />
          </motion.div>

          {/* Bottom tagline */}
          <motion.p
            className="mt-8 text-[9px] uppercase tracking-[0.4em] text-slate-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            You Play · We Trade · 25+ Years
          </motion.p>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function tick(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
